import { SandboxVisbility } from "components/ShareButton/ShareSandbox/components/SandboxVisibility";
import { useEditorFeature, useSandboxEditor } from "features/editor/Feature";
import type { PermissionType } from "features/types/sandbox";
import type { SandboxInfoTab } from "features/types/tabs";
import { hasSandboxPermission } from "isomorphic/permission";
import { debounce } from "lodash";
import { Input, Text, Stack, TextArea, Checkbox, Divider } from "prism-react";
import {
  useChangeSandboxInfo,
  useChangeTemplateInfo,
  useCreateTemplate,
  useDeleteTemplate,
} from "queries/sandbox/api";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { useAnalytics } from "hooks/useAnalytics";

import { DevtoolWrapper } from "../common/DevtoolWrapper";

import { TemplatePicker } from "./TemplatePicker";

export const SandboxInfoUI: React.FC<{
  title: string;
  description?: string;
  isTemplate: boolean;
  isFrozen: boolean;
  isCloud: boolean;
  loading: boolean;
  onIsTemplateChanged: (isTemplate: boolean) => void;
  onIsFrozenChanged: (isFrozen: boolean) => void;
  onDidTemplateIconUrlChange: (templateIconUrl: string | undefined) => void;
  onSubmit: (value: { title: string; description: string }) => void;
  permission: PermissionType;
  templateIconUrl: string | undefined;
  tab: SandboxInfoTab;
  panelId: string;
}> = ({
  onSubmit,
  isFrozen,
  isCloud,
  onIsFrozenChanged,
  title,
  description,
  isTemplate,
  onIsTemplateChanged,
  onDidTemplateIconUrlChange,
  permission,
  templateIconUrl,
  loading,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ title: string; description: string }>({
    defaultValues: {
      title,
      description,
    },
  });

  const boxType = isCloud ? "devbox" : "sandbox";
  const hasWritePermission = hasSandboxPermission(permission, "write_project");

  return (
    <DevtoolWrapper variant="setup">
      <Stack
        as="form"
        css={{ px: "$2" }}
        gap={6}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack gap={2}>
          <Text color="neutral-fg-medium">Title</Text>
          <Input
            variant={errors.title ? "error" : "base"}
            {...register("title")}
            defaultValue={title}
            disabled={loading}
            icon={loading ? "progress" : undefined}
            iconPosition="end"
            onBlur={handleSubmit(onSubmit)}
            placeholder="Sandbox name"
          />
          {errors.title && (
            <Text color="informative-error-base">This field is required</Text>
          )}
        </Stack>

        <Stack gap={2}>
          <Text color="neutral-fg-medium">Description</Text>
          <TextArea
            disabled={loading}
            variant={errors.description ? "error" : "base"}
            {...register("description")}
            defaultValue={description}
            onBlur={handleSubmit(onSubmit)}
          />
          {errors.description && (
            <Text color="informative-error-base">This field is required</Text>
          )}
        </Stack>

        <Stack gap={2}>
          <Text color="neutral-fg-medium">Visibility</Text>
          <SandboxVisbility hasPermission={hasWritePermission} />
        </Stack>

        <Stack gap={2}>
          <Text color="neutral-fg-medium">Protected</Text>
          <Text
            as="label"
            css={{
              display: "flex",
              alignItems: "center",
              gap: "$2",
            }}
          >
            <Checkbox
              checked={isFrozen}
              disabled={loading}
              onCheckedChange={(checked) => {
                onIsFrozenChanged(!!checked);
              }}
            />
            Protected {boxType}es cannot be modified unless they are forked.
          </Text>
        </Stack>

        <Divider />

        <Stack gap={2}>
          <Text color="neutral-fg-medium">Template</Text>
          <Text
            as="label"
            css={{
              display: "flex",
              alignItems: "center",
              gap: "$2",
            }}
          >
            <Checkbox
              checked={isTemplate}
              onCheckedChange={(isChecked) => {
                onIsTemplateChanged(Boolean(isChecked));
              }}
            />
            Use this {boxType} as a template.
          </Text>
        </Stack>

        {isTemplate && (
          <Stack gap={2}>
            <Text color="neutral-fg-subtle">Icon</Text>
            <Stack horizontal>
              <TemplatePicker
                selectIcon={(iconUrl) => {
                  onDidTemplateIconUrlChange(iconUrl);
                }}
                templateIconUrl={templateIconUrl}
                title={title}
              />
            </Stack>
          </Stack>
        )}

        <Divider />
      </Stack>

      <input type="submit" hidden />
    </DevtoolWrapper>
  );
};

const SandboxInfoImple: React.FC<{
  tab: SandboxInfoTab;
  panelId: string;
}> = ({ tab, panelId }) => {
  const [, dispatchProject] = useEditorFeature();
  const editor = useSandboxEditor();
  const track = useAnalytics();

  const [optimisticIsFrozen, setOptimisticIsFrozen] = useState<null | boolean>(
    null,
  );
  const [optimisticIconUrl, setOptimisticIconUrl] = useState<null | string>(
    null,
  );

  const [values, setValues] = useState<{
    title: string | undefined | null;
    description: string | undefined | null;
    isTemplate: boolean;
    isFrozen: boolean;
  }>({
    title: editor.title,
    description: editor.description,
    isTemplate: Boolean(editor.customTemplate?.id),
    isFrozen: editor.isFrozen,
  });
  const [loading, setLoading] = useState(false);
  const updateInfo = useChangeSandboxInfo(editor.id);
  const createTemplate = useCreateTemplate(editor.id);
  const deleteTemplate = useDeleteTemplate(editor.id);
  const updateTemplate = useChangeTemplateInfo(
    editor.id,
    editor.customTemplate?.id,
  );

  const handleIsTemplateChanged = async (isTemplate: boolean) => {
    if (isTemplate) {
      // Create template
      const customTemplate = await createTemplate(
        title || "",
        editor.description || "",
        "#fffff",
      );
      dispatchProject({
        type: "UPDATE_SANDBOX",
        project: {
          customTemplate,
        },
      });
    } else {
      const templateId = editor.customTemplate?.id;
      if (templateId) {
        // Delete template
        await deleteTemplate(templateId);
      }
      dispatchProject({
        type: "UPDATE_SANDBOX",
        project: {
          customTemplate: null,
        },
      });
    }
  };

  const debouncedSubmit = debounce(
    async (newValues) => {
      if (
        (newValues.title && newValues.title !== values.title) ||
        newValues.description !== values.description
      ) {
        setLoading(true);

        const updatedSandbox = await updateInfo({
          title: newValues.title,
          description: newValues.description,
        });

        dispatchProject({
          type: "UPDATE_SANDBOX",
          project: {
            // Always get this back when changing title
            alias: updatedSandbox.alias!,
            // We changed the title
            title: updatedSandbox.title!,

            description: updatedSandbox.description,
          },
        });

        setLoading(false);
      }

      setValues(newValues);
    },
    500,
    {
      trailing: true,
    },
  );

  const title = editor.title;

  const templateIconUrl =
    editor.customTemplate?.iconUrl || editor.forkedTemplate?.iconUrl;

  return (
    <SandboxInfoUI
      description={editor.description}
      isCloud={editor.isCloud}
      isFrozen={optimisticIsFrozen ?? editor.isFrozen}
      isTemplate={Boolean(editor.customTemplate?.id)}
      loading={loading}
      onDidTemplateIconUrlChange={(templateIconUrl) => {
        track("update_sandbox_template_icon", {
          event_source: "sandbox info pane",
          iconUrl: templateIconUrl,
        });

        if (templateIconUrl) {
          setOptimisticIconUrl(templateIconUrl);
        }
        updateTemplate({
          iconUrl: templateIconUrl,
        })
          .then(() => {
            dispatchProject({
              type: "UPDATE_SANDBOX",
              project: {
                customTemplate: {
                  ...editor.customTemplate!,
                  iconUrl: templateIconUrl,
                },
              },
            });
          })
          .finally(() => {
            setOptimisticIconUrl(null);
          });
      }}
      onIsFrozenChanged={(isFrozen) => {
        setOptimisticIsFrozen(isFrozen);
        updateInfo({
          is_frozen: isFrozen,
        })
          .then(() => {
            dispatchProject({
              type: "UPDATE_SANDBOX",
              project: {
                isFrozen,
              },
            });
          })
          .finally(() => {
            setOptimisticIsFrozen(null);
          });
      }}
      onIsTemplateChanged={handleIsTemplateChanged}
      onSubmit={debouncedSubmit}
      panelId={panelId}
      permission={editor.authorization}
      tab={tab}
      templateIconUrl={optimisticIconUrl ?? templateIconUrl}
      title={title}
    />
  );
};

export const SandboxInfo: React.FC<{
  tab: SandboxInfoTab;
  panelId: string;
}> = ({ tab, panelId }) => {
  const editor = useSandboxEditor();

  return <SandboxInfoImple key={editor.id} panelId={panelId} tab={tab} />;
};
